import React, { FC } from 'react';
import { brand, colors, tokens } from '../../theme/theme';
import { Button } from '@primer/react';
import { CSSObject } from 'styled-components';

type Variant =
  | 'secondary'
  | 'juicyPrimary'
  | 'juicySecondary'
  | 'juicyTertiary'
  | 'sparkPrimary'
  | 'sparkSecondary'
  | 'sparkTertiary'
  | 'sparkDisabled'
  | 'realPrimary'
  | 'realSecondary'
  | 'realTertiary'
  | 'realDisabled';
type ButtonProps = {
  readonly variant?: Variant;
  readonly size?: 'large' | 'small';
  [key: string]: any;
};

// Flat pills as on the website, in each game mode's colours.
// Primary: solid fill. Secondary: tinted fill with a 2px outline (the
// border width comes from index.css).
// Tertiary: white with a 2px outline.
const filled = (bg: string, hoverBg: string, color = '#fff'): CSSObject => ({
  backgroundColor: bg,
  borderColor: bg,
  color,
  boxShadow: 'var(--shadow-soft)',
  '&:hover:not([disabled])': {
    backgroundColor: hoverBg,
    borderColor: hoverBg,
    color,
  },
});

const outlined = (bg: string, outline: string, hoverBg: string, color = outline): CSSObject => ({
  backgroundColor: bg,
  borderColor: outline,
  color,
  boxShadow: 'none',
  '&:hover:not([disabled])': {
    backgroundColor: hoverBg,
    borderColor: outline,
    color,
  },
});

const disabled = (bg: string, color: string): CSSObject => ({
  backgroundColor: bg,
  borderColor: color,
  color,
  boxShadow: 'none',
});

// Exported so plain Primer Buttons can use the same pill styles in sx.
export const cjrButtonStyles: Record<Variant, CSSObject> = {
  // The website's secondary button: purple outline, no fill.
  secondary: outlined(tokens.background, brand.purple, brand.lavender),
  juicyPrimary: filled(tokens.accent.DEFAULT, 'hsl(27, 100%, 55%)'),
  juicySecondary: outlined(colors.juicy.muted, colors.accent.emphasis, tokens.accent.peach.bg),
  juicyTertiary: outlined('#fff', colors.accent.emphasis, tokens.accent.peach.bgSoft),
  sparkPrimary: filled(colors.spark.btn, colors.spark.other),
  sparkSecondary: outlined(colors.spark.muted, colors.spark.dark, brand.lavender),
  sparkTertiary: outlined('#fff', colors.spark.btn, colors.spark.muted),
  sparkDisabled: disabled(colors.spark.muted, colors.spark.new),
  realPrimary: filled(colors.real.btn, '#c00046'),
  realSecondary: outlined(colors.real.muted, colors.real.btn, '#ffeae4'),
  realTertiary: outlined('#fff', colors.real.btn, '#ffeae4'),
  realDisabled: disabled('#fff', 'lightgray'),
};

export const CJRButton: FC<ButtonProps> = ({ variant = 'juicyPrimary', size, ...props }) => {
  return (
    <Button
      size={size ?? 'large'}
      sx={{ width: '100%', ...cjrButtonStyles[variant] }}
      type="button"
      {...props}
    >
      {props.children}
    </Button>
  );
};
